import React from "react";
import { useTranslation } from "react-i18next";
import { ListChecks, Plus, Trophy, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import QuizQuestionCard from "@/components/QuizQuestionCard";

const TIME_OPTIONS = [0, 10, 15, 20, 30, 45, 60];

function newQuestion() {
  return {
    id: (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()),
    text: "",
    multiple: false,
    answers: [
      { text: "", correct: true },
      { text: "", correct: false },
      { text: "", correct: false },
    ],
    link_url: "",
    link_label: "",
  };
}

/** QuizEditor — embedded in AdminGameEditor when game.type === "quiz". */
export default function QuizEditor({ game, onGameChange }) {
  const { t } = useTranslation();
  const questions = game.quiz_questions || [];
  const setQuestions = (qs) => onGameChange({ quiz_questions: qs });

  function move(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= questions.length) return;
    const next = [...questions];
    [next[i], next[j]] = [next[j], next[i]];
    setQuestions(next);
  }

  return (
    <div className="qrise-card p-6 space-y-5" data-testid="quiz-editor">
      <div className="flex items-center gap-2">
        <ListChecks className="h-4 w-4 text-violet-500" />
        <div className="text-sm font-semibold">{t("game_editor.quiz_title")}</div>
      </div>
      <p className="text-[11px] text-muted-foreground leading-snug -mt-3">{t("game_editor.quiz_hint")}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium inline-flex items-center gap-1">
            <Trophy className="h-3 w-3" />
            {t("game_editor.winners_count")}
          </Label>
          <Input
            type="number"
            min={1}
            value={game.winners_count ?? 1}
            onChange={(e) => onGameChange({ winners_count: Math.max(1, parseInt(e.target.value, 10) || 1) })}
            className="mt-1.5 rounded-xl h-10"
            data-testid="quiz-winners-count-input"
          />
          <p className="text-[11px] text-muted-foreground leading-snug mt-1.5">{t("game_editor.quiz_winners_hint")}</p>
        </div>
        <div>
          <Label className="text-xs font-medium inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {t("game_editor.quiz_time")}
          </Label>
          <Select
            value={String(game.quiz_time_per_question || 0)}
            onValueChange={(v) => onGameChange({ quiz_time_per_question: parseInt(v, 10) })}
          >
            <SelectTrigger className="mt-1.5 rounded-xl h-10" data-testid="quiz-time-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_OPTIONS.map((s) => (
                <SelectItem key={s} value={String(s)}>
                  {s === 0 ? t("game_editor.quiz_time_none") : t("game_editor.quiz_time_seconds", { seconds: s })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground leading-snug mt-1.5">{t("game_editor.quiz_time_hint")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          ["quiz_show_expected_count", "quiz_show_expected"],
          ["quiz_unlimited_plays", "quiz_unlimited"],
        ].map(([field, key]) => (
          <label key={field} className="flex items-center justify-between gap-3 rounded-xl border border-border/60 px-3 py-2 cursor-pointer">
            <div className="text-xs">
              <div className="font-semibold">{t(`game_editor.${key}`)}</div>
              <div className="text-[11px] text-muted-foreground">{t(`game_editor.${key}_hint`)}</div>
            </div>
            <Switch
              checked={!!game[field]}
              onCheckedChange={(v) => onGameChange({ [field]: v })}
              data-testid={`quiz-${field.replace(/_/g, "-")}-switch`}
            />
          </label>
        ))}
      </div>

      <div className="space-y-3">
        <div>
          <Label className="text-xs font-medium">{t("game_editor.quiz_retry_message")}</Label>
          <Textarea
            value={game.quiz_retry_message || ""}
            onChange={(e) => onGameChange({ quiz_retry_message: e.target.value || null })}
            placeholder={t("play.quiz_fail_retry_body")}
            rows={2}
            className="mt-1.5 rounded-xl text-sm"
            data-testid="quiz-retry-message-input"
          />
          <p className="text-[11px] text-muted-foreground leading-snug mt-1.5">{t("game_editor.quiz_retry_message_hint")}</p>
        </div>
        {questions.map((q, i) => (
          <QuizQuestionCard
            key={q.id || i}
            question={q}
            index={i}
            total={questions.length}
            onChange={(p) => setQuestions(questions.map((x, j) => (j === i ? { ...x, ...p } : x)))}
            onRemove={() => setQuestions(questions.filter((_, j) => j !== i))}
            onMove={(dir) => move(i, dir)}
          />
        ))}
        {questions.length === 0 && (
          <div className="rounded-xl border-2 border-dashed border-border p-6 text-center text-xs text-muted-foreground" data-testid="quiz-no-questions">
            {t("game_editor.quiz_no_questions")}
          </div>
        )}
      </div>

      <Button type="button" variant="outline" className="w-full h-10 rounded-xl text-xs font-semibold"
        onClick={() => setQuestions([...questions, newQuestion()])} data-testid="quiz-add-question-btn">
        <Plus className="h-3.5 w-3.5 mr-1" />
        {t("game_editor.quiz_add_question")}
      </Button>
    </div>
  );
}
